export type BookingProviderType =
  | "TIMELY365"
  | "GOOGLE_CALENDAR"
  | "MICROSOFT_OUTLOOK"
  | "CALENDLY"
  | "EXTERNAL_URL"
  | "MANUAL_REQUEST"
  | "NONE";

export type BookingProviderConfig = {
  type: BookingProviderType;
  externalUrl?: string;
};

export type BookingProviderConnection = {
  providerType?: BookingProviderType | string | null;
  enabled?: boolean | null;
  managementUrl?: string | null;
  publicBookingUrl?: string | null;
  externalUrl?: string | null;
  providerResourceId?: string | null;
  capabilities?: BookingProviderCapabilities | null;
};

export type BookingProviderCapabilities = {
  key: BookingProviderType | string;
  label: string;
  availabilityMode: string;
  bookingMode: string;
  changeMode: string;
  automaticAvailability: boolean;
  automaticBooking: boolean;
  automaticUpdates: boolean;
  supportsBookOnBehalf: boolean;
  requiresOAuth: boolean;
};

const STORAGE_KEY = "planyBookingProviderByWorkspace";
const DEFAULT_CONFIG: BookingProviderConfig = Object.freeze({ type: "TIMELY365" });

function normalizeType(value: unknown): BookingProviderType {
  const normalized = String(value || "").trim().toUpperCase();
  if (normalized === "GOOGLE_CALENDAR") return "GOOGLE_CALENDAR";
  if (normalized === "MICROSOFT_OUTLOOK") return "MICROSOFT_OUTLOOK";
  if (normalized === "CALENDLY") return "CALENDLY";
  if (normalized === "EXTERNAL_URL") return "EXTERNAL_URL";
  if (normalized === "MANUAL_REQUEST") return "MANUAL_REQUEST";
  if (normalized === "NONE") return "NONE";
  return "TIMELY365";
}

function normalizeConfig(value: any): BookingProviderConfig {
  const type = normalizeType(value?.type || value?.providerType);
  const externalUrl = String(value?.externalUrl || value?.bookingUrl || "").trim();
  return externalUrl ? { type, externalUrl } : { type };
}

function readLocalConfigs(): Record<string, BookingProviderConfig> {
  if (typeof window === "undefined") return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function getBookingProviderConfig(
  business: any,
  businessId?: string | number | null
): BookingProviderConfig {
  // Prefer the future backend contract whenever it becomes available.
  const serverConfig =
    business?.bookingProvider ||
    business?.businessBasic?.bookingProvider ||
    business?.careSettings?.bookingProvider ||
    business?.businessBasic?.careSettings?.bookingProvider;
  if (serverConfig) return normalizeConfig(serverConfig);

  const key = String(businessId || business?.businessBasic?.id || "").trim();
  if (key) {
    const localConfig = readLocalConfigs()[key];
    if (localConfig) return normalizeConfig(localConfig);
  }
  return { ...DEFAULT_CONFIG };
}

export function saveLocalBookingProviderConfig(
  businessId: string | number,
  config: BookingProviderConfig
): BookingProviderConfig {
  const key = String(businessId || "").trim();
  if (!key) throw new Error("A workspace is required to save a booking provider.");
  const normalized = normalizeConfig(config);
  if (typeof window !== "undefined") {
    const current = readLocalConfigs();
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...current, [key]: normalized })
    );
  }
  return normalized;
}

export function isValidExternalBookingUrl(value: string): boolean {
  try {
    const url = new URL(String(value || "").trim());
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

/**
 * Resolve provider-owned availability management without exposing provider UI
 * routes to Care screens. Timely's current booking-type identifier is treated
 * as an opaque provider resource ID so this adapter can later move server-side.
 */
export function buildBookingProviderAvailabilityUrl(
  connection: BookingProviderConnection | null | undefined,
  providerBookingTypeId?: string | number | null
): string | null {
  if (!connection || connection.enabled === false) return null;

  const providerType = normalizeType(connection.providerType);
  if (new Set(["EXTERNAL_URL", "CALENDLY", "GOOGLE_CALENDAR", "MICROSOFT_OUTLOOK"]).has(providerType)) {
    return String(
      connection.managementUrl || connection.externalUrl || connection.publicBookingUrl || ""
    ).trim() || null;
  }
  if (providerType !== "TIMELY365") return null;

  const managementUrl = String(connection.managementUrl || "").trim();
  if (!managementUrl) return null;
  const resourceId = String(providerBookingTypeId || "").trim();
  if (!resourceId) return managementUrl;

  try {
    const url = new URL(managementUrl);
    url.pathname = `/biz/update/${encodeURIComponent(resourceId)}`;
    return url.toString();
  } catch {
    return managementUrl;
  }
}
